import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type Quest } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, StatTile, type Column } from '@/components/ui';
import { IconAward } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';
import { Pager } from '@/components/admin/Pager';
import { Drawer, Field } from '@/components/admin/Drawer';

const PAGE = 25;

/** Quests — the driver app's weekly-quest / "Opportunities" feed (`driver_quests`,
 *  owned by incentives-svc). Mirrors the legacy Filament DriverQuestResource
 *  (title / subtitle / day-of-week / ends-at / status) as a clean light
 *  list + detail VIEW.
 *
 *  READ-ONLY (honest, no dead buttons): incentives-svc exposes admin authoring
 *  endpoints for campaigns and promos but NOT for `driver_quests`, so admin-svc
 *  has nothing to proxy a create/edit/delete to (and never writes another
 *  service's table directly). A plain banner states the reason; create/edit
 *  lights up the day incentives-svc grows quest authoring.
 *
 *  DEFENSIVE: shows a "not yet available" state when the `driver_quests` table
 *  isn't in this Postgres yet (incentives-svc migration 025 not applied). */
export function Quests() {
  const [offset, setOffset] = useState(0);
  // Filter to active quests only (status = 1) — mirrors the status column in the
  // legacy resource as a useful list affordance (backend-supported `active`).
  const [activeOnly, setActiveOnly] = useState(false);
  // The quest id whose detail drawer is open (null = closed).
  const [selected, setSelected] = useState<number | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'quests', activeOnly, offset],
    queryFn: ({ signal }) =>
      adminApi.quests({ active: activeOnly ? 1 : undefined, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const unavailable = data && data.available === false;
  const rows = useMemo(() => data?.items ?? [], [data]);

  // Page-local summary tiles. The list is paged, so "active on page" is honest
  // about scope (it counts the rows actually loaded, not a global aggregate),
  // while "total" reflects the full matched set from the server.
  const activeOnPage = useMemo(() => rows.filter((q) => q.status === 1).length, [rows]);

  const columns: Array<Column<Quest>> = [
    { key: 'id', header: 'ID', cell: (r) => <span className="metric text-xs text-text-muted">#{r.id}</span> },
    {
      key: 'title',
      header: 'Quest',
      cell: (r) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-slate-100">{r.title}</div>
          {r.subtitle ? <div className="truncate text-xs text-text-secondary">{r.subtitle}</div> : null}
        </div>
      ),
    },
    { key: 'day', header: 'Schedule', cell: (r) => <span className="text-xs text-slate-300">{r.day_label}</span> },
    { key: 'ends', header: 'Ends', cell: (r) => <span className="text-xs text-text-secondary">{dateTime(r.ends_at)}</span> },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => (
        <Badge tone={r.status === 1 ? 'ok' : 'neutral'} dot>
          {r.status === 1 ? 'active' : 'inactive'}
        </Badge>
      ),
    },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r.id)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-hover"
        >
          View
        </button>
      ),
    },
  ];

  return (
    <AdminPage>
      <AdminPageHeader
        title="Quests"
        subtitle="Driver weekly quests — the Opportunities feed the driver app reads (owned by incentives-svc)."
        right={
          <label className="flex items-center gap-2 text-xs text-text-secondary">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => {
                setActiveOnly(e.target.checked);
                setOffset(0);
              }}
              className="h-4 w-4 rounded border-ink-700 text-brand-500 focus:ring-brand-500"
            />
            Active only
          </label>
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Quests not yet in Postgres"
            description={
              data?.note ??
              'The driver_quests table is owned by incentives-svc and is not in this Postgres yet (migration 025 not applied here).'
            }
            icon={<IconAward className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <>
          {/* Honest read-only banner — this view has no create/edit because the
              owning service (incentives-svc) exposes no quest authoring endpoint
              to proxy. No dead buttons; the reason is stated plainly. The server
              also surfaces this as `writable_note`. */}
          <div className="mb-4 rounded-lg border border-ink-800 bg-ink-850 px-3.5 py-2.5 text-xs text-text-secondary">
            Read-only view. Quest authoring isn’t wired yet —{' '}
            <span className="text-slate-300">incentives-svc</span> owns{' '}
            <span className="metric">driver_quests</span> and doesn’t expose admin create/edit/delete
            endpoints (it does for campaigns &amp; promos). This lists the live quests; create/edit lights up
            once incentives-svc grows quest authoring. Tier targets &amp; bonuses live on the{' '}
            <span className="text-slate-300">Quest Tiers</span> page.
          </div>

          {!isLoading && !isError ? (
            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
              <StatTile
                label={activeOnly ? 'Active quests' : 'Total quests'}
                value={data?.total ?? 0}
                hint={activeOnly ? 'status = active' : 'all statuses'}
                icon={<IconAward className="h-4 w-4" />}
              />
              <StatTile
                label="Active on page"
                value={activeOnPage}
                tone={activeOnPage > 0 ? 'ok' : 'neutral'}
                hint={`of ${rows.length} shown`}
              />
              <StatTile label="On this page" value={rows.length} hint={`page size ${PAGE}`} />
            </div>
          ) : null}

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading quests…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load quests" description={(error as Error)?.message} />
            ) : (
              <>
                <DataTable<Quest>
                  columns={columns}
                  rows={rows}
                  rowKey={(r) => String(r.id)}
                  stickyHeader
                  empty={
                    <EmptyState
                      title="No quests"
                      description={
                        activeOnly
                          ? 'No active driver quests right now.'
                          : 'No driver quests have been created yet.'
                      }
                      icon={<IconAward className="h-6 w-6" />}
                    />
                  }
                />
                <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
              </>
            )}
          </Card>
        </>
      )}

      {selected !== null ? <QuestDrawer id={selected} onClose={() => setSelected(null)} /> : null}
    </AdminPage>
  );
}

/** Read-only detail drawer for one quest. Fetches the single row (any status)
 *  and renders its fields. No edit form — the page is a read view. */
function QuestDrawer({ id, onClose }: { id: number; onClose: () => void }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'quest', id],
    queryFn: ({ signal }) => adminApi.quest(id, signal),
  });

  const quest = data?.quest;

  return (
    <Drawer
      title={quest ? quest.title : `Quest #${id}`}
      subtitle={quest?.subtitle || 'Driver quest detail'}
      onClose={onClose}
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner label="Loading quest…" />
        </div>
      ) : isError ? (
        <EmptyState title="Couldn't load quest" description={(error as Error)?.message} />
      ) : !quest ? (
        <EmptyState
          title="Quest unavailable"
          description={data?.note ?? 'This quest could not be loaded.'}
          icon={<IconAward className="h-6 w-6" />}
        />
      ) : (
        <div className="space-y-4">
          <Card>
            <div>
              <Field label="ID" value={`#${quest.id}`} />
              <Field label="Title" value={quest.title} />
              <Field label="Subtitle" value={quest.subtitle || '—'} />
              <Field label="Schedule" value={quest.day_label} />
              <Field label="Ends" value={dateTime(quest.ends_at)} />
              <Field
                label="Status"
                value={
                  <Badge tone={quest.status === 1 ? 'ok' : 'neutral'} dot>
                    {quest.status === 1 ? 'active' : 'inactive'}
                  </Badge>
                }
              />
              <Field label="Created" value={dateTime(quest.created_at)} />
            </div>
          </Card>

          <p className="text-[11px] text-text-muted">
            Read-only. Quests are authored upstream in incentives-svc; this console lists them but cannot
            edit them yet. Each quest’s tier targets &amp; bonuses are managed on the Quest Tiers page.
          </p>
        </div>
      )}
    </Drawer>
  );
}
